/**
 * DocumentViewerModal
 *
 * Lightweight shared modal for viewing an archived artifact copy in place of
 * the (frequently dead) external "View original" source link.
 *
 * Fetches a Supabase signed URL for the artifact's storage_path via
 * GET /api/artifacts/:id/download, then:
 *   - PDF (kind === 'pdf'): renders the signed URL in a browser-native
 *     <iframe> so the file is viewable inline, no plugin required.
 *   - Any other kind (v1 scope): no inline preview — Download only.
 *
 * Always offers a Download button (re-uses the same signed URL).
 */
import { useEffect, useState } from 'react';
import { Loader2, X, Download, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/apiFetch';

export interface DocumentViewerModalProps {
  artifactId: string;
  /** Original source URL for direct download. */
  sourceUrl: string;
  title?: string;
  /** Artifact `kind` — only 'pdf' gets an inline preview in v1. */
  kind?: string;
  onClose: () => void;
}

export function DocumentViewerModal({
  artifactId,
  sourceUrl,
  title,
  kind,
  onClose,
}: DocumentViewerModalProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Inline (non-attachment) URL for preview — the endpoint defaults
        // to forcing a Content-Disposition: attachment, which prevents the
        // browser from rendering the PDF inside an <iframe>.
        const previewRes = await apiFetch(
          `/api/artifacts/${artifactId}/download?download=false`
        );
        if (!previewRes.ok) throw new Error(`HTTP ${previewRes.status}`);
        const previewJson = (await previewRes.json()) as {
          data?: { url?: string };
        };
        const url = previewJson.data?.url;
        if (!url) throw new Error('No signed URL returned');
        if (!cancelled) setSignedUrl(url);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [artifactId]);

  const isPdf = (kind ?? '').toLowerCase() === 'pdf';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-4xl h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-red-500 shrink-0" />
            <p className="font-semibold text-gray-900 truncate">
              {title ?? 'Document'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                window.open(sourceUrl, '_blank', 'noopener,noreferrer')
              }
              className="gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </Button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
            <p className="font-semibold text-gray-700">
              Could not load document
            </p>
            <p className="text-sm text-gray-500 mt-1">{error}</p>
          </div>
        ) : isPdf && signedUrl ? (
          <iframe
            src={signedUrl}
            title={title ?? 'Document preview'}
            className="flex-1 w-full border-0"
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <FileText className="w-12 h-12 text-gray-200 mb-3" />
            <p className="font-medium text-gray-700">
              Preview not available for this file type
            </p>
            <p className="text-sm text-gray-500 mt-1 max-w-sm">
              Inline preview currently supports PDF documents only. Use the
              Download button above to view this file.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
